import React, { useState, useCallback, memo, MouseEvent, Fragment } from "react";
import { ProductType } from "@appTypes/productType";
import { User } from "@appTypes/user";
import { Popup } from "@progress/kendo-react-popup";
import styles from "./UserProductsCell.module.css";

interface UserWithProducts extends User {
  assignedProducts?: ProductType[];
}

interface UserProductsCellProps {
  user: UserWithProducts;
}

const ProductIcon = memo(({ product, onMouseEnter, onMouseLeave }: { 
  product: ProductType; 
  onMouseEnter: (e: MouseEvent<HTMLImageElement>, product: ProductType) => void;
  onMouseLeave: () => void;
}) => (
  <img
    className={styles.productIcon}
    alt={`${product.name} Product Logo`}
    src={`/assets/productIcons/${product.productLogoFileName || "icon-default.png"}`}
    onMouseEnter={(e) => onMouseEnter(e, product)}
    onMouseLeave={onMouseLeave}
  />
));

ProductIcon.displayName = 'ProductIcon';

const UserProductsCell = ({ user }: UserProductsCellProps) => {
  const [hoveredProduct, setHoveredProduct] = useState<ProductType | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const handleMouseEnter = useCallback((event: MouseEvent<HTMLImageElement>, product: ProductType) => {
    setHoveredProduct(product);
    setAnchor(event.currentTarget);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredProduct(null);
    setAnchor(null);
  }, []);

  if (!user.assignedProducts || user.assignedProducts.length === 0) {
    return <div className={styles.productContainer}><span>No products assigned</span></div>;
  }

  return (
    <div className={styles.productContainer}>
      {user.assignedProducts?.map((product) => (
        <Fragment key={product.productGuid}>
          <ProductIcon product={product} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} />
          
          {hoveredProduct?.productGuid === product.productGuid && anchor && (
            <Popup
              anchor={anchor}
              show={true}
              className={styles.productTooltip}
              animate={false}
            >
              <div className={styles.tooltipContent}>
                {product.name}
                {product.subName && <span className={styles.subName}>{product.subName}</span>}
              </div>
            </Popup>
          )}
        </Fragment>
      ))}
    </div>
  );
};

export default memo(UserProductsCell);
export { UserProductsCell };